import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '../lib/auth'
import { listSurroundings } from '../lib/endpoints/surroundings'
import type { SurroundingsHistoryEntry } from './SurroundingsHistory'

type PreviewState = {
  organizationId: string | undefined
  organizationName: string
  entries: readonly SurroundingsHistoryEntry[]
  selectedId: string | null
  select: (id: string) => void
  newDialogOpen: boolean
  setNewDialogOpen: (open: boolean) => void
  historyOpen: boolean
  setHistoryOpen: (open: boolean) => void
  startNew: () => void
  addQuickPreview: (query: string) => void
  updatePreviewAddress: (address: string, query: string) => void
  rememberRun: (runId: string, jobId: string) => void
  historyError: string | null
}
const PreviewContext = createContext<PreviewState | undefined>(undefined)

export function SurroundingsPreviewProvider({
  children,
}: {
  children: ReactNode
}) {
  const auth = useAuth()
  const organizationId =
    auth.status === 'authenticated' ? auth.user.active_org?.id : undefined
  return (
    <ScopedPreview
      key={organizationId ?? 'no-organization'}
      organizationId={organizationId}
    >
      {children}
    </ScopedPreview>
  )
}

function ScopedPreview({
  organizationId,
  children,
}: {
  organizationId: string | undefined
  children: ReactNode
}) {
  const [serverEntries, setServerEntries] = useState<
    SurroundingsHistoryEntry[]
  >([])
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [historyRevision, refreshHistory] = useState(0)
  useEffect(() => {
    if (!organizationId) return
    let current = true
    listSurroundings()
      .then((rows) => {
        if (!current) return
        setServerEntries(
          rows.map((row) => ({
            id: row.runId,
            runId: row.runId,
            jobId: row.jobId,
            organizationId,
            address: row.projectName,
            description: 'Zapisana generacja',
            scene: 'quick',
            query:
              row.location.kind === 'coordinates'
                ? `${row.location.latitude}, ${row.location.longitude}`
                : row.location.address,
          })),
        )
        setHistoryError(null)
      })
      .catch((error) => {
        if (current)
          setHistoryError(
            error instanceof Error
              ? error.message
              : 'Nie odczytano historii generacji.',
          )
      })
    return () => {
      current = false
    }
  }, [organizationId, historyRevision])
  const [localEntries, setLocalEntries] = useState<SurroundingsHistoryEntry[]>(
    [],
  )
  const [previewAddress, setPreviewAddress] = useState<
    Record<string, { address: string; query: string }>
  >({})
  const entries = useMemo(
    () =>
      [
        ...localEntries.filter(
          (entry) =>
            !entry.runId ||
            !serverEntries.some((server) => server.runId === entry.runId),
        ),
        ...serverEntries.map((server) => ({
          ...server,
          id:
            localEntries.find((local) => local.runId === server.runId)?.id ??
            server.id,
        })),
      ].map((entry) => ({
        ...entry,
        ...previewAddress[entry.id],
      })),
    [localEntries, serverEntries, previewAddress],
  )
  const [selectedId, setSelectedId] = useState<string | null>(
    entries[0]?.id ?? null,
  )
  useEffect(() => {
    if (!selectedId && entries.length) setSelectedId(entries[0].id)
  }, [entries, selectedId])
  const rememberRun = useCallback(
    (runId: string, jobId: string) => {
      const selected = entries.find((entry) => entry.id === selectedId)
      if (!selected) return
      if (selected.runId && selected.runId !== runId) {
        // A subsequent generation is a separate history item. Retain the old
        // result and reopen the accepted job rather than overwriting its entry.
        setLocalEntries((previous) => [
          { ...selected, id: runId, runId, jobId },
          ...previous,
        ])
        setSelectedId(runId)
      } else {
        setLocalEntries((previous) =>
          previous.map((entry) =>
            entry.id === selectedId ? { ...entry, runId, jobId } : entry,
          ),
        )
      }
      refreshHistory((value) => value + 1)
    },
    [entries, selectedId],
  )
  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const select = useCallback(
    (id: string) => {
      if (entries.some((entry) => entry.id === id)) setSelectedId(id)
      setHistoryOpen(false)
    },
    [entries],
  )
  const startNew = useCallback(() => {
    setHistoryOpen(false)
    setNewDialogOpen(true)
  }, [])
  const addQuickPreview = useCallback(
    (query: string) => {
      if (!organizationId) return
      const entry = {
        id: crypto.randomUUID(),
        organizationId,
        address: query,
        query,
        scene: 'quick',
        description: 'Podgląd otoczenia · tylko ta sesja',
      }
      setLocalEntries((previous) => [entry, ...previous].slice(0, 30))
      setSelectedId(entry.id)
      setNewDialogOpen(false)
    },
    [organizationId],
  )
  const updatePreviewAddress = useCallback(
    (address: string, query: string) => {
      if (selectedId)
        setPreviewAddress((previous) =>
          previous[selectedId]?.address === address &&
          previous[selectedId]?.query === query
            ? previous
            : { ...previous, [selectedId]: { address, query } },
        )
    },
    [selectedId],
  )
  const value = useMemo(
    () => ({
      organizationId,
      organizationName: 'Bieżąca organizacja',
      entries,
      selectedId,
      select,
      newDialogOpen,
      setNewDialogOpen,
      historyOpen,
      setHistoryOpen,
      startNew,
      addQuickPreview,
      updatePreviewAddress,
      rememberRun,
      historyError,
    }),
    [
      organizationId,
      entries,
      selectedId,
      select,
      newDialogOpen,
      historyOpen,
      startNew,
      addQuickPreview,
      updatePreviewAddress,
      rememberRun,
      historyError,
    ],
  )
  return (
    <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>
  )
}

export function useSurroundingsPreview() {
  const value = useContext(PreviewContext)
  if (!value)
    throw new Error('Surroundings preview requires its organization provider')
  return value
}
